/**
 * Service for interacting with baby profile API endpoints
 */
import { logger } from '../utils/logger';
import { calculateAge } from '../utils/dateUtils';

export const babyProfileService = {
  /**
   * Get all baby profiles for the current user
   * @param {string} token - The user's authentication token
   * @returns {Promise<Array>} - A promise that resolves to an array of baby profiles
   */
  async getBabyProfiles(token) {
    try {
      const apiUrl = process.env.REACT_APP_BABY_PROFILES_API_URL;
      if (!apiUrl) {
        throw new Error('API URL not found in environment variables');
      }

      const response = await fetch(`${apiUrl}/baby-profiles`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch baby profiles: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching baby profiles:', error);
      throw error;
    }
  },
  
  /**
   * Create a new baby profile
   * @param {string} token - The user's authentication token
   * @param {Object} profileData - The profile data to create
   * @returns {Promise<Object>} - A promise that resolves to the created baby profile
   * @throws {Error} - If the user has reached their plan limit
   */
  async createBabyProfile(token, profileData) {
    try {
      const apiUrl = process.env.REACT_APP_BABY_PROFILES_API_URL;
      if (!apiUrl) {
        throw new Error('API URL not found in environment variables');
      }

      // Initialize editCount to 0 for new profiles
      const dataWithEditCount = {
        ...profileData,
        editCount: 0
      };

      const response = await fetch(`${apiUrl}/baby-profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataWithEditCount)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        
        // If this is a plan limit error (403 status)
        if (response.status === 403 && errorData) {
          throw {
            status: response.status,
            ...errorData
          };
        }
        
        throw new Error(`Failed to create baby profile: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error creating baby profile:', error);
      throw error;
    }
  },

  /**
   * Get a specific baby profile by ID
   * @param {string} token - The user's authentication token
   * @param {string} profileId - The ID of the baby profile to fetch
   * @returns {Promise<Object>} - A promise that resolves to the baby profile
   */
  async getBabyProfile(token, profileId) {
    try {
      const apiUrl = process.env.REACT_APP_BABY_PROFILES_API_URL;
      if (!apiUrl) {
        throw new Error('API URL not found in environment variables');
      }

      logger.debug(`Fetching baby profile with ID: ${profileId} from ${apiUrl}/baby-profiles/${profileId}`);
      
      const response = await fetch(`${apiUrl}/baby-profiles/${profileId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch baby profile: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      logger.debug('Baby profile API response:', data);
      return data;
    } catch (error) {
      logger.error('Error fetching baby profile:', error);
      throw error;
    }
  },

  /**
   * Update a baby profile
   * @param {string} token - The user's authentication token
   * @param {string} profileId - The ID of the baby profile to update
   * @param {Object} profileData - The updated profile data
   * @returns {Promise<Object>} - A promise that resolves to the updated baby profile
   */
  async updateBabyProfile(token, profileId, profileData) {
    try {
      const apiUrl = process.env.REACT_APP_BABY_PROFILES_API_URL;
      if (!apiUrl) {
        throw new Error('API URL not found in environment variables');
      }

      // First get the current profile to check edit count
      const currentProfile = await this.getBabyProfile(token, profileId);
      const currentEditCount = currentProfile.editCount || 0;
      
      // Check if edit limit has been reached
      if (currentEditCount >= 3) {
        throw new Error('Edit limit reached: Profile can only be edited 3 times');
      }
      
      // Include incremented editCount in the update data
      const updatedProfileData = {
        ...profileData,
        editCount: currentEditCount + 1
      };
      
      logger.debug(`Updating profile ${profileId}, incrementing edit count from ${currentEditCount} to ${updatedProfileData.editCount}`);

      const response = await fetch(`${apiUrl}/baby-profiles/${profileId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedProfileData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update baby profile: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error updating baby profile:', error);
      throw error;
    }
  },

  /**
   * Calculate the age of a child in months and years
   * @param {string} dateOfBirth - The child's date of birth in ISO format
   * @returns {string} - A formatted string representing the child's age
   */
  calculateAge(dateOfBirth) {
    return calculateAge(dateOfBirth);
  }
};
